import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const PermissionsContext = createContext({ permissions: {}, loading: true });

// Map route paths to module permission keys
const ROUTE_TO_MODULE = {
  "/dashboard": "dashboard",
  "/executive": "executive",
  "/location-pulse": "location_pulse",
  "/command-center": "command_center",
  "/calendar": "calendar",
  "/waitlist": "waitlist",
  "/events": "events",
  "/clients": "clients",
  "/client-scores": "client_scores",
  "/score-alerts": "score_alerts",
  "/noshow-risk": "noshow_risk",
  "/staff": "staff",
  "/team-hub": "team_hub",
  "/team-performance": "team_performance",
  "/training": "training",
  "/timeclock": "timeclock",
  "/timesheets": "timeclock",
  "/shift-briefing": "shift_briefing",
  "/payroll": "payroll",
  "/pos": "pos",
  "/memberships": "memberships",
  "/loyalty": "loyalty",
  "/referrals": "referrals",
  "/gift-cards": "gift_cards",
  "/inventory": "inventory",
  "/marketing": "marketing",
  "/rebook": "rebook",
  "/automations": "automations",
  "/reviews": "reviews",
  "/forms": "forms",
  "/reports": "reports",
  "/settings": "settings",
  "/integrations": "integrations",
  "/webhooks": "webhooks",
  "/white-label": "white_label",
};

// Map sidebar nav labels to module keys
const NAV_LABEL_TO_MODULE = {
  "Dashboard": "dashboard",
  "Executive": "executive",
  "Location Pulse": "location_pulse",
  "Command Center": "command_center",
  "Calendar": "calendar",
  "Waitlist": "waitlist",
  "Events & Groups": "events",
  "All Clients": "clients",
  "Scores": "client_scores",
  "Alerts": "score_alerts",
  "No-Show Risk": "noshow_risk",
  "Staff": "staff",
  "Team Hub": "team_hub",
  "Performance": "team_performance",
  "Training": "training",
  "Time Clock": "timeclock",
  "Timesheets": "timeclock",
  "Shift Briefing": "shift_briefing",
  "Payroll": "payroll",
  "Checkout": "pos",
  "Memberships": "memberships",
  "Loyalty": "loyalty",
  "Referrals": "referrals",
  "Gift Cards": "gift_cards",
  "Inventory": "inventory",
  "Campaigns": "marketing",
  "Due for Rebook": "rebook",
  "Automations": "automations",
  "Reviews": "reviews",
  "Forms": "forms",
  "Reports": "reports",
  "Settings": "settings",
  "Integrations": "integrations",
  "Webhooks": "webhooks",
  "White Label": "white_label",
};

// Group labels map to whether ANY child is visible
const NAV_GROUP_MODULES = {
  "Schedule": ["calendar", "waitlist", "events"],
  "Clients": ["clients", "client_scores", "score_alerts", "noshow_risk"],
  "Team": ["staff", "team_hub", "team_performance", "training", "timeclock", "shift_briefing", "payroll"],
  "Sales": ["pos", "memberships", "loyalty", "referrals", "gift_cards", "inventory"],
  "Marketing": ["marketing", "rebook", "automations", "reviews", "forms"],
  "Insights": ["reports", "executive", "location_pulse", "command_center"],
  "Admin": ["settings", "integrations", "webhooks", "white_label"],
};

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState({});
  const [accessLevel, setAccessLevel] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) { setPermissions({}); setLoading(false); return; }
    const load = async () => {
      try {
        const res = await api.get("/my/permissions");
        setPermissions(res.data.permissions || {});
        setAccessLevel(res.data.access_level || "");
      } catch {
        // Fallback: owner gets everything
        setPermissions({});
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const canView = useCallback((module) => {
    if (!module) return true;
    if (!permissions || Object.keys(permissions).length === 0) return true; // No perms loaded = allow
    return permissions[module]?.view !== false;
  }, [permissions]);

  const canCreate = useCallback((module) => {
    if (!module) return true;
    if (!permissions || Object.keys(permissions).length === 0) return true;
    return permissions[module]?.create !== false;
  }, [permissions]);

  const canEdit = useCallback((module) => {
    if (!module) return true;
    if (!permissions || Object.keys(permissions).length === 0) return true;
    return permissions[module]?.edit !== false;
  }, [permissions]);

  const canDelete = useCallback((module) => {
    if (!module) return true;
    if (!permissions || Object.keys(permissions).length === 0) return true;
    return permissions[module]?.delete !== false;
  }, [permissions]);

  const canViewRoute = useCallback((path) => {
    const module = ROUTE_TO_MODULE[path];
    return canView(module);
  }, [canView]);

  const canViewNavItem = useCallback((label) => {
    const module = NAV_LABEL_TO_MODULE[label];
    return canView(module);
  }, [canView]);

  const canViewNavGroup = useCallback((groupLabel) => {
    const mods = NAV_GROUP_MODULES[groupLabel];
    if (!mods) return true;
    return mods.some(m => canView(m));
  }, [canView]);

  return (
    <PermissionsContext.Provider value={{
      permissions, accessLevel, loading,
      canView, canCreate, canEdit, canDelete,
      canViewRoute, canViewNavItem, canViewNavGroup,
      ROUTE_TO_MODULE, NAV_LABEL_TO_MODULE,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
